import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Mail, 
  Shield, 
  Database, 
  Users, 
  Bell, 
  Save, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Smartphone,
  Globe,
  Lock,
  Activity,
  DollarSign
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { useToast } from '../hooks/use-toast';
import { apiService } from '../services/api';

const AdminSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [systemStatus, setSystemStatus] = useState({
    backend: 'checking',
    database: 'checking',
    email_service: 'checking',
    total_users: 0,
    active_users: 0
  });

  const [settings, setSettings] = useState({
    // Email Settings
    email_notifications_enabled: true,
    email_rate_limit: 100,
    email_sender_name: 'FlameLink Team',
    
    // SMS Settings
    sms_notifications_enabled: false,
    sms_rate_limit: 50,
    
    // User Management
    auto_verify_new_users: false,
    require_email_verification: true,
    max_photos_per_user: 6,
    allow_photo_uploads: true,
    
    // Dating System
    max_swipes_per_day: 100,
    max_tip_amount: 1000,
    min_tip_amount: 1,
    enable_super_likes: true,
    enable_tipping_system: true,
    
    // Security
    session_timeout_hours: 24,
    max_login_attempts: 5,
    require_strong_passwords: true,
    
    // Content Moderation
    auto_moderate_messages: false,
    flagged_content_action: 'review',
    
    // System Maintenance
    maintenance_mode: false,
    maintenance_message: 'FlameLink is currently under maintenance. Please check back soon!',
    
    // Analytics
    track_user_activity: true,
    data_retention_days: 365
  });

  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
    checkSystemStatus();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.admin.getSettings();
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: "Error",
        description: "Failed to load admin settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const checkSystemStatus = async () => {
    try {
      // Get system status from admin API
      const statusResponse = await apiService.admin.getSystemStatus();
      if (statusResponse.data.success) {
        const status = statusResponse.data.data;
        setSystemStatus({
          backend: status.backend,
          database: status.database,
          email_service: status.email_service,
          total_users: status.total_users,
          active_users: status.active_users
        });
      }
    } catch (error) {
      console.error('Error checking system status:', error);
      setSystemStatus(prev => ({
        ...prev,
        backend: 'error',
        database: 'error',
        email_service: 'error'
      }));
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      const response = await apiService.admin.updateSettings(settings);
      
      if (response.data.success) {
        toast({
          title: "Settings Saved! ✅",
          description: "Admin settings have been updated successfully."
        });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save admin settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'checking': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'error': return <AlertCircle className="w-4 h-4" />;
      case 'checking': return <RefreshCw className="w-4 h-4 animate-spin" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* System Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            System Status
          </CardTitle>
          <CardDescription>
            Current health status of FlameLink system components
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Backend API</p>
                <p className={`text-xs ${getStatusColor(systemStatus.backend)}`}>
                  {systemStatus.backend === 'healthy' ? 'Online' : 
                   systemStatus.backend === 'error' ? 'Offline' : 'Checking...'}
                </p>
              </div>
              <div className={getStatusColor(systemStatus.backend)}>
                {getStatusIcon(systemStatus.backend)}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Database</p>
                <p className={`text-xs ${getStatusColor(systemStatus.database)}`}>
                  {systemStatus.database === 'healthy' ? 'Connected' : 
                   systemStatus.database === 'error' ? 'Disconnected' : 'Checking...'}
                </p>
              </div>
              <div className={getStatusColor(systemStatus.database)}>
                {getStatusIcon(systemStatus.database)}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Email Service</p>
                <p className={`text-xs ${getStatusColor(systemStatus.email_service)}`}>
                  {systemStatus.email_service === 'healthy' ? 'Operational' : 
                   systemStatus.email_service === 'error' ? 'Error' : 'Checking...'}
                </p>
              </div>
              <div className={getStatusColor(systemStatus.email_service)}>
                {getStatusIcon(systemStatus.email_service)}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Active Users</p>
                <p className="text-xs text-gray-600">{systemStatus.active_users} / {systemStatus.total_users}</p>
              </div>
              <Users className="w-4 h-4 text-blue-600" />
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={checkSystemStatus}
              className="flex items-center"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Refresh Status
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bell className="w-5 h-5 mr-2" />
            Notification System
          </CardTitle>
          <CardDescription>
            Configure email and SMS notification settings for users
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Email Notifications</Label>
                  <p className="text-xs text-gray-500">Enable email notifications system-wide</p>
                </div>
                <Switch
                  checked={settings.email_notifications_enabled}
                  onCheckedChange={(checked) => updateSetting('email_notifications_enabled', checked)}
                />
              </div>

              {settings.email_notifications_enabled && (
                <>
                  <div>
                    <Label htmlFor="email_rate_limit">Email Rate Limit (per hour)</Label>
                    <Input
                      id="email_rate_limit"
                      type="number"
                      value={settings.email_rate_limit}
                      onChange={(e) => updateSetting('email_rate_limit', parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email_sender_name">Sender Name</Label>
                    <Input
                      id="email_sender_name"
                      value={settings.email_sender_name}
                      onChange={(e) => updateSetting('email_sender_name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">SMS Notifications</Label>
                  <p className="text-xs text-gray-500">Enable SMS notifications (requires Twilio)</p>
                </div>
                <Switch
                  checked={settings.sms_notifications_enabled}
                  onCheckedChange={(checked) => updateSetting('sms_notifications_enabled', checked)}
                />
              </div>

              {settings.sms_notifications_enabled && (
                <div>
                  <Label htmlFor="sms_rate_limit">SMS Rate Limit (per hour)</Label>
                  <Input
                    id="sms_rate_limit"
                    type="number"
                    value={settings.sms_rate_limit}
                    onChange={(e) => updateSetting('sms_rate_limit', parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dating System Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Dating System Settings
          </CardTitle>
          <CardDescription>
            Configure tipping, swipes, and matching system parameters
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Enable Tipping System</Label>
                  <p className="text-xs text-gray-500">Allow users to send tip offers</p>
                </div>
                <Switch
                  checked={settings.enable_tipping_system}
                  onCheckedChange={(checked) => updateSetting('enable_tipping_system', checked)}
                />
              </div>

              {settings.enable_tipping_system && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="min_tip">Min Tip Amount ($)</Label>
                      <Input
                        id="min_tip"
                        type="number"
                        value={settings.min_tip_amount}
                        onChange={(e) => updateSetting('min_tip_amount', parseFloat(e.target.value))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="max_tip">Max Tip Amount ($)</Label>
                      <Input
                        id="max_tip"
                        type="number"
                        value={settings.max_tip_amount}
                        onChange={(e) => updateSetting('max_tip_amount', parseFloat(e.target.value))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="max_swipes">Max Swipes per Day</Label>
                <Input
                  id="max_swipes"
                  type="number"
                  value={settings.max_swipes_per_day}
                  onChange={(e) => updateSetting('max_swipes_per_day', parseInt(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Enable Super Likes</Label>
                  <p className="text-xs text-gray-500">Allow users to send super likes</p>
                </div>
                <Switch
                  checked={settings.enable_super_likes}
                  onCheckedChange={(checked) => updateSetting('enable_super_likes', checked)}
                />
              </div>

              <div>
                <Label htmlFor="max_photos">Max Photos per User</Label>
                <Input
                  id="max_photos"
                  type="number"
                  value={settings.max_photos_per_user}
                  onChange={(e) => updateSetting('max_photos_per_user', parseInt(e.target.value))}
                  className="mt-1"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Allow Photo Uploads</Label>
                  <p className="text-xs text-gray-500">Users can upload profile photos</p>
                </div>
                <Switch
                  checked={settings.allow_photo_uploads}
                  onCheckedChange={(checked) => updateSetting('allow_photo_uploads', checked)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Management Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="w-5 h-5 mr-2" />
            User Management
          </CardTitle>
          <CardDescription>
            Configure user registration and verification settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Auto-verify New Users</Label>
                  <p className="text-xs text-gray-500">Automatically verify new registrations</p>
                </div>
                <Switch
                  checked={settings.auto_verify_new_users}
                  onCheckedChange={(checked) => updateSetting('auto_verify_new_users', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Require Email Verification</Label>
                  <p className="text-xs text-gray-500">Users must verify email to access app</p>
                </div>
                <Switch
                  checked={settings.require_email_verification}
                  onCheckedChange={(checked) => updateSetting('require_email_verification', checked)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Require Strong Passwords</Label>
                  <p className="text-xs text-gray-500">Enforce password complexity rules</p>
                </div>
                <Switch
                  checked={settings.require_strong_passwords}
                  onCheckedChange={(checked) => updateSetting('require_strong_passwords', checked)}
                />
              </div>

              <div>
                <Label htmlFor="session_timeout">Session Timeout (hours)</Label>
                <Input
                  id="session_timeout"
                  type="number"
                  value={settings.session_timeout_hours}
                  onChange={(e) => updateSetting('session_timeout_hours', parseInt(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Maintenance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Settings className="w-5 h-5 mr-2" />
            System Maintenance
          </CardTitle>
          <CardDescription>
            Configure system-wide maintenance and operational settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Maintenance Mode</Label>
              <p className="text-xs text-gray-500">
                Put the system in maintenance mode
                {settings.maintenance_mode && <Badge variant="destructive" className="ml-2">ACTIVE</Badge>}
              </p>
            </div>
            <Switch
              checked={settings.maintenance_mode}
              onCheckedChange={(checked) => updateSetting('maintenance_mode', checked)}
            />
          </div>

          {settings.maintenance_mode && (
            <div>
              <Label htmlFor="maintenance_message">Maintenance Message</Label>
              <Textarea
                id="maintenance_message"
                value={settings.maintenance_message}
                onChange={(e) => updateSetting('maintenance_message', e.target.value)}
                className="mt-1"
                rows="3"
              />
            </div>
          )}

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Track User Activity</Label>
                <p className="text-xs text-gray-500">Enable analytics and activity tracking</p>
              </div>
              <Switch
                checked={settings.track_user_activity}
                onCheckedChange={(checked) => updateSetting('track_user_activity', checked)}
              />
            </div>

            <div>
              <Label htmlFor="data_retention">Data Retention (days)</Label>
              <Input
                id="data_retention"
                type="number"
                value={settings.data_retention_days}
                onChange={(e) => updateSetting('data_retention_days', parseInt(e.target.value))}
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end space-x-2">
        <Button
          variant="outline"
          onClick={loadSettings}
          disabled={loading || saving}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Reset
        </Button>
        
        <Button
          onClick={saveSettings}
          disabled={loading || saving}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;